import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";
import type { ExamPrescriptionTemplateInput } from "@/lib/validations/exam-prescription";
import i18n from "@/i18n";

type ExamPrescriptionTemplateRow = Database["public"]["Tables"]["exam_prescription_templates"]["Row"];
type ExamPrescriptionTemplateItemRow =
  Database["public"]["Tables"]["exam_prescription_template_items"]["Row"];
type ClinicScopedExamPrescriptionTemplateRow = Omit<ExamPrescriptionTemplateRow, "clinic_id"> & {
  clinic_id: string;
  owner_user_id: string;
};

export type ExamPrescriptionTemplateWithItems = ClinicScopedExamPrescriptionTemplateRow & {
  items: ExamPrescriptionTemplateItemRow[];
};

export const useExamPrescriptionTemplates = (clinicId?: string | null) => {
  return useQuery({
    queryKey: ["exam-prescription-templates", clinicId],
    enabled: Boolean(clinicId),
    queryFn: async () => {
      if (!clinicId) return [] as ExamPrescriptionTemplateWithItems[];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("exam_prescription_templates")
        .select("*, items:exam_prescription_template_items(*)")
        .eq("clinic_id", clinicId)
        .order("title", { ascending: true });

      if (error) throw error;
      return (data ?? []) as ExamPrescriptionTemplateWithItems[];
    },
  });
};

export const useCreateExamPrescriptionTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ExamPrescriptionTemplateInput & { clinic_id: string }) => {
      const { data: auth, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      const userId = auth.user?.id ?? null;
      if (!userId) throw new Error(i18n.t("prescriptions:templates.errors.notAuthenticated"));
      const clinicId = payload.clinic_id?.trim();
      if (!clinicId) throw new Error(i18n.t("prescriptions:templates.feedback.createError"));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: template, error } = await (supabase as any)
        .from("exam_prescription_templates")
        .insert({
          clinic_id: clinicId,
          title: payload.title.trim(),
          observations: payload.observations?.trim() || null,
          created_by: userId,
          owner_user_id: userId,
        })
        .select()
        .single();

      if (error) throw error;

      if (payload.items.length > 0) {
        const itemInserts: Database["public"]["Tables"]["exam_prescription_template_items"]["Insert"][] =
          payload.items.map((item) => ({
            template_id: template.id,
            exam_type_id: item.exam_type_id && item.exam_type_id !== "" ? item.exam_type_id : null,
            exam_name: item.exam_name,
            instructions: item.instructions || null,
          }));

        const { error: itemsError } = await supabase
          .from("exam_prescription_template_items")
          .insert(itemInserts);

        if (itemsError) throw itemsError;
      }

      return template;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["exam-prescription-templates", variables.clinic_id] });
      toast.success(i18n.t("prescriptions:templates.feedback.examCreated"));
    },
    onError: (error) => {
      console.error("Erro ao criar template de exame:", error);
      toast.error(i18n.t("prescriptions:templates.feedback.createError"));
    },
  });
};

export const useUpdateExamPrescriptionTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      clinicId,
      payload,
    }: {
      templateId: string;
      clinicId: string;
      payload: ExamPrescriptionTemplateInput;
    }) => {
      if (!clinicId) throw new Error(i18n.t("prescriptions:templates.feedback.updateError"));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from("exam_prescription_templates")
        .update({
          title: payload.title.trim(),
          observations: payload.observations?.trim() || null,
        })
        .eq("id", templateId)
        .eq("clinic_id", clinicId);

      if (error) throw error;

      const { error: deleteError } = await supabase
        .from("exam_prescription_template_items")
        .delete()
        .eq("template_id", templateId);

      if (deleteError) throw deleteError;

      if (payload.items.length > 0) {
        const itemInserts: Database["public"]["Tables"]["exam_prescription_template_items"]["Insert"][] =
          payload.items.map((item) => ({
            template_id: templateId,
            exam_type_id: item.exam_type_id && item.exam_type_id !== "" ? item.exam_type_id : null,
            exam_name: item.exam_name,
            instructions: item.instructions || null,
          }));

        const { error: itemsError } = await supabase
          .from("exam_prescription_template_items")
          .insert(itemInserts);

        if (itemsError) throw itemsError;
      }

      return templateId;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["exam-prescription-templates", variables.clinicId] });
      toast.success(i18n.t("prescriptions:templates.feedback.examUpdated"));
    },
    onError: (error) => {
      console.error("Erro ao atualizar template de exame:", error);
      toast.error(i18n.t("prescriptions:templates.feedback.updateError"));
    },
  });
};

export const useDeleteExamPrescriptionTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ templateId, clinicId }: { templateId: string; clinicId: string }) => {
      if (!clinicId) throw new Error(i18n.t("prescriptions:templates.feedback.deleteError"));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from("exam_prescription_templates")
        .delete()
        .eq("id", templateId)
        .eq("clinic_id", clinicId);
      if (error) throw error;
      return templateId;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["exam-prescription-templates", variables.clinicId] });
      toast.success(i18n.t("prescriptions:templates.feedback.deleted"));
    },
    onError: (error) => {
      console.error("Erro ao remover template de exame:", error);
      toast.error(i18n.t("prescriptions:templates.feedback.deleteError"));
    },
  });
};
